import * as React from "react";
import { ReactNode, useMemo } from "react";
import styles from "./table.module.css";
import NoResultImage from "./images/table-no-results-available.svg";
import { CoreSpinner } from "../progress-indicators/Spinner";
import { CorePagination } from "../pagination/Pagination";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow, { TableRowProps } from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { createTheme, Skeleton, ThemeProvider, Tooltip, Typography } from "@mui/material";
import TableSortLabel from "@mui/material/TableSortLabel";
import CaretUp from "./images/caret-up.svg?react";
import CaretDown from "./images/caret-down.svg?react";
import { themeOptions } from "../../theme";

type Order = "asc" | "desc";

type RestrictedTableRowProps = Omit<TableRowProps, "onClick">;

export type CoreTableRow<D extends object> = {
	/** The data displayed in the row. */
	readonly data: D;
	/** Function that toggles whether the row's subcomponent is rendered.
	 *  If `value` is passed, the subcomponent will be rendered when `true`.
	 */
	readonly toggleRowExpanded: (value?: boolean) => void;
	/** The expanded state of the row's subcomponent. `true` if subcomponent is rendered. */
	readonly isExpanded: boolean;
	/** Whether the row has a subcomponent. */
	readonly canExpand: boolean;
	/** Relative index of the row within the current page. */
	readonly index: number;
	/** Absolute index of the row in the table */
	readonly absoluteIndex: number;
	/** Index of the pagination page that the row is in */
	readonly paginationPage?: number;
	/** Number of visible rows per pagination page */
	readonly rowsPerPage?: number;
};
type RowRender<D extends object> =
	| React.ReactNode
	| (() => React.ReactNode)
	| ((row: CoreTableRow<D>) => React.ReactNode);

export type CoreTableSubRow<D extends object, P extends object> = {
	/** The data displayed in the sub row. */
	readonly data: D;
	/** The parent data of the sub rpw. */
	readonly parentData: P;
	/** Function that toggles whether the row's subcomponent is rendered.
	 *  If `value` is passed, the subcomponent will be rendered when `true`.
	 */
	readonly toggleRowExpanded: (value?: boolean) => void;
	/** The expanded state of the row's subcomponent. `true` if subcomponent is rendered. */
	readonly isExpanded: boolean;
	/** Whether the row has a subcomponent. */
	readonly canExpand: boolean;
	/** Index of the row in the table. */
	readonly index: number;
	/** Sub index of the sub row in the row in the table. */
	readonly subIndex: number;
};
type SubRowRender<D extends object, P extends object> =
	| React.ReactNode
	| (() => React.ReactNode)
	| ((row: CoreTableSubRow<D, P>) => React.ReactNode);

type G<Key, Data> = Key extends keyof Data
	? Data[Key] extends Array<infer U>
		? Data[Key][0] extends U & object
			? Key
			: never
		: never
	: never;
type B<Key, Data> = Key extends keyof Data
	? Data[Key] extends Array<infer U>
		? Data[Key][0] extends U & object
			? Data[Key][0]
			: never
		: never
	: never;

export type CoreTableColumn<D extends object, K extends keyof D = keyof D> = {
	/** The header text of the column. */
	header?: React.ReactNode;
	/** A unique identifier for the column. */
	key: React.Key;
	/** A component or function that determines the contents of the column.
	 *  @param row Contains the data, and other properties, of the row.
	 *  @returns A component to render in each cell of the column's rows.
	 */
	cell: RowRender<D>;
	/** A component or function that determines the contents of the column's row's subrow column.
	 *  @param row Contains the data, and other properties, of the row.
	 *  @returns A component to render in each cell of the column's rows's subrows.
	 */
	subRowCell?: SubRowRender<B<K, D>, D>;
	/** Width of the column. */
	width?: string | number;
	/** Minimum width of the column. */
	minWidth?: string | number;
	/** Width of the column. */
	maxWidth?: string | number;
	/** Width of the content inside a table cell */
	tableCellContentWidth?: string | number;
	/** The background color of the column in the table body. @default inherit */
	bodyBackgroundColor?: string;
	/** Padding of the cells. */
	padding?: string | number;
	/** The font size. */
	fontSize?: string | number;
	/** The header font size. */
	headerFontSize?: string | number;
	/** The number of cells the column spans. @default 1 */
	colSpan?: number;
	/** The number of cells the column's header spans. @default 1 */
	colSpanHeader?: number;
	/** The number of cells the column's body spans. @default 1 */
	colSpanBody?: number;
	/** The number of cells the column's sub row spans. @default 1 */
	colSpanSubRow?: number;
	/** Set the text-align on the column's table cell content. @default 'left' */
	align?: TableCellProps["align"];
	/** Set the text-align on the column's header cell content. @default `align` */
	alignHeader?: TableCellProps["align"];
	/** Set the text-align on the column's body table cell content. @default `align` */
	alignBody?: TableCellProps["align"];
	/** Set the vertical-align on the column's table cell content. @default 'middle' */
	valign?: TableCellProps["valign"] | ((data: CoreTableRow<D>) => TableCellProps["valign"]);
	/** If defined, the column may be sorted in ascending or descending order.
	 *  The comparator used to sort the table by the current column.
	 */
	descendingComparator?: (a: D, b: D) => number;
	/** Used for strings. If `true`, the cell contents will truncate and not wrap or clip. */
	truncateString?: boolean;
	/** If `true`, the column will not render. */
	hide?: boolean;
	/** If `true` and `descendingComparator` is defined, the table sort label will be disabled. */
	tableSortLabelDisabled?: boolean;
	/** Signify if column body cells should allow wrapping @default false*/
	allowBodyWrap?: boolean;
	/** Signify if column header cell should allow wrapping @default false*/
	allowHeaderWrap?: boolean;
	/** Signify if column body cells should provide a tooltip @default false */
	showTooltip?: boolean;
	/** Signify if the column should be sticky @default false */
	sticky?: boolean;
};

function LoadingRow(props: { colSpan: number }) {
	return (
		<TableRow key={"spinning-wheel-row"}>
			<TableCell colSpan={props.colSpan}>
				<div id="spinning-wheel" key="spinning-wheel" className={styles["loading-wrapper"]}>
					<CoreSpinner size={80} />
				</div>
			</TableCell>
		</TableRow>
	);
}

/**
 *
 * Generates a skeleton table while the data has not loaded
 *
 */
function LoadingRowSkeleton(props: { allColSpans: number[]; rowsPerPage?: number }) {
	return Array(props.rowsPerPage ? props.rowsPerPage : 5)
		.fill(0)
		.map((_, rowIndex) => (
			<TableRow key={rowIndex}>
				{props.allColSpans.map((colSpan, colIndex) => (
					<TableCell colSpan={colSpan} key={colIndex}>
						<Skeleton animation="wave" variant="rounded" />
					</TableCell>
				))}
			</TableRow>
		));
}

function NoResultsRow(props: {
	noResultsTitle?: ReactNode;
	noResultsString: string;
	hideNoResultsImage: boolean;
	colSpan: number;
}) {
	return (
		<TableRow key={"no-results-row"}>
			<TableCell colSpan={props.colSpan}>
				<div id="no-results" key="no-results" className={styles["no-results"]}>
					{!props.hideNoResultsImage && <img src={NoResultImage} className={styles["no-results-img"]} alt="" />}
					<div className={styles["no-results-text"]}>
						{props.noResultsTitle && <div className={styles["no-results-title"]}>{props.noResultsTitle}</div>}
						<p className={styles["no-results-p"]}>{props.noResultsString}</p>
					</div>
				</div>
			</TableCell>
		</TableRow>
	);
}

/**
 * Generates the header row
 * @returns <React.Fragment>
 */
function HeaderContent<D extends object, K extends keyof D = keyof D>({
	columns,
	onRequestSort,
	orderBy,
	order,
	defaultSortOrder,
	stickyColumnOffsets,
	headerCellRefs,
}: {
	columns: CoreTableColumn<D, K>[];
	onRequestSort: (
		event: React.MouseEvent<unknown>,
		key: React.Key,
		descendingComparator: (a: D, b: D) => number,
	) => void;
	orderBy: React.Key | null;
	order: Order;
	headerBackgroundColor?: string;
	defaultSortOrder?: Order;
	stickyColumnOffsets?: Record<string, number>;
	headerCellRefs?: React.MutableRefObject<Record<string, HTMLTableCellElement | null>>;
}) {
	const createSortHandler =
		(key: React.Key, descendingComparator: (a: D, b: D) => number) => (event: React.MouseEvent<unknown>) => {
			onRequestSort(event, key, descendingComparator);
		};

	return (
		<TableRow>
			{columns.map((column) => {
				return column.hide ? null : (
					<TableCell
						sx={{
							width: column.width,
							minWidth: column.minWidth,
							padding: column.padding,
							textOverflow: column.allowHeaderWrap ? "revert" : "inherit",
							textWrap: column.allowHeaderWrap ? "wrap" : "inherit",
							overflow: column.allowHeaderWrap ? "revert" : "inherit",
							...(column.sticky &&
								stickyColumnOffsets && {
									position: "sticky",
									right: stickyColumnOffsets[column.key.toString()],
									zIndex: 3,
									backgroundColor: "inherit",
								}),
						}}
						key={column.key}
						variant="head"
						align={column.alignHeader ?? column.align ?? "left"}
						colSpan={column.colSpanHeader ?? column.colSpan ?? 1}
						sortDirection={orderBy === column.key ? order : false}
						aria-sort={orderBy === column.key ? (order === "asc" ? "ascending" : "descending") : undefined}
						ref={(element) => {
							if (headerCellRefs?.current) {
								headerCellRefs.current[column.key.toString()] = element as HTMLTableCellElement;
							}
						}}
					>
						{column.descendingComparator ? (
							<TableSortLabel
								active={orderBy === column.key}
								direction={orderBy === column.key ? order : (defaultSortOrder ?? "asc")}
								onClick={createSortHandler(column.key, column.descendingComparator)}
								IconComponent={(props) => <TableSortLabelIcon {...props} />}
								disabled={column.tableSortLabelDisabled}
							>
								{column.header}
							</TableSortLabel>
						) : (
							column.header
						)}
					</TableCell>
				);
			})}
		</TableRow>
	);
}

/**
 * Generates a sort icon (Caret)
 * @returns <React.Fragment>
 */
export function TableSortLabelIcon({ className, size }: { className: string; size?: string | number }) {
	return (
		<Box
			display="flex"
			flexDirection="column"
			gap="4px"
			height={size ?? "16px"}
			width={size ?? "12px"} // 12px to remove extra white space which is causing gap in focus outline
			alignItems="center"
			justifyContent="center"
			className={className}
			sx={{
				"&&": {
					opacity: 1,
					transform: "none",
				},
				"& .VerityTableSortLabelIcon": {
					color: "#8B8B8B",
				},
				".Mui-disabled & .VerityTableSortLabelIcon": {
					color: "#0002",
				},
				".Mui-active:not(.Mui-disabled) &.MuiTableSortLabel-iconDirectionAsc .VerityTableSortLabelIconAsc": {
					color: "#454545",
				},
				".Mui-active:not(.Mui-disabled) &.MuiTableSortLabel-iconDirectionDesc .VerityTableSortLabelIconDesc": {
					color: "#454545",
				},
			}}
		>
			<CaretUp className="VerityTableSortLabelIcon VerityTableSortLabelIconAsc" />
			<CaretDown className="VerityTableSortLabelIcon VerityTableSortLabelIconDesc" />
		</Box>
	);
}

/**
 * Generates one rows and its subrows
 * @returns <React.Fragment>
 */
function BodyRowContent<D extends object, K extends keyof D = keyof D>({
	columns,
	rowData,
	index,
	absoluteIndex,
	renderRowSubComponent,
	rowProps,
	defaultRowExpanded,
	subRowDataProp,
	totalColSpan,
	keepClosed,
	noSubComponentPadding,
	hover,
	paginationPage,
	rowsPerPage,
	unhoverableRows,
	stickyColumnOffsets,
	onRowClick,
}: {
	columns: CoreTableColumn<D, K>[];
	rowData: D;
	index: number;
	absoluteIndex: number;
	renderRowSubComponent?: RowRender<D>;
	rowProps?: Partial<RestrictedTableRowProps> | ((row: CoreTableRow<D>) => Partial<RestrictedTableRowProps>);
	defaultRowExpanded?: boolean;
	subRowDataProp?: G<K, D>;
	totalColSpan: number;
	keepClosed: boolean;
	noSubComponentPadding?: boolean;
	hover?: boolean;
	paginationPage: number;
	rowsPerPage: number;
	unhoverableRows?: number[];
	stickyColumnOffsets?: Record<string, number>;
	onRowClick?: (row: CoreTableRow<D>) => void;
}) {
	const [open, setOpen] = React.useState(defaultRowExpanded ?? false);

	const toggleOpen = (value?: boolean) => {
		return value ? setOpen(value) : setOpen(!open);
	};

	React.useEffect(() => {
		if (keepClosed) {
			setOpen(false);
		}
	}, [keepClosed]);

	const row: CoreTableRow<D> = {
		data: rowData,
		toggleRowExpanded: toggleOpen,
		isExpanded: open,
		canExpand: Boolean(renderRowSubComponent),
		index: index,
		paginationPage: paginationPage,
		rowsPerPage: rowsPerPage,
		absoluteIndex: absoluteIndex,
	};

	const isRowHoverable = hover && !unhoverableRows?.includes(row.absoluteIndex);
	const resolvedRowProps = typeof rowProps === "function" ? rowProps(row) : rowProps;

	let interactiveProps: Partial<TableRowProps> = {};
	if (onRowClick) {
		interactiveProps = {
			role: "button",
			tabIndex: 0,
			hover: true,
			onClick: () => onRowClick(row),
			onKeyDown: (e) => {
				if (e.key == "Enter" || e.key === " ") {
					e.preventDefault();
					onRowClick(row);
				}
				resolvedRowProps?.onKeyDown?.(e as React.KeyboardEvent<HTMLTableRowElement>);
			},
			sx: {
				cursor: "pointer",
				"&:focus": {
					outline: "2px solid #0b6f85",
					outlineOffset: "-2px",
				},
				...resolvedRowProps?.sx,
			},
		};
	}

	let subRowList: React.ReactNode[] = [];
	if (subRowDataProp) {
		const subRowArr = row.data[subRowDataProp];
		if (Array.isArray(subRowArr)) {
			subRowList = subRowArr.map((subRowData, subIndex) => {
				const subRow = {
					data: subRowData,
					parentData: row.data,
					toggleRowExpanded: toggleOpen,
					isExpanded: open,
					canExpand: Boolean(renderRowSubComponent),
					index: index,
					subIndex: subIndex,
				};

				return (
					<TableRow key={`${index}-row-${subIndex}`} hover={isRowHoverable}>
						{columns.map((column) => {
							const valign = typeof column.valign === "function" ? column.valign(row) : column.valign;

							return column.hide || column.subRowCell === null ? null : (
								<TableCell
									key={`${index}-${column.key}-${subIndex}`}
									align={column.alignBody ?? column.align ?? "left"}
									valign={valign}
									colSpan={column.colSpanSubRow ?? column.colSpan ?? 1}
									sx={{
										width: column.width,
										minWidth: column.minWidth,
										padding: column.padding,
										verticalAlign: valign,
										textOverflow: column.allowBodyWrap ? "revert" : "inherit",
										textWrap: column.allowBodyWrap ? "wrap" : "inherit",
										overflow: column.allowBodyWrap ? "revert" : "inherit",
										...(column.sticky &&
											stickyColumnOffsets && {
												position: "sticky",
												right: stickyColumnOffsets[column.key.toString()],
												zIndex: 1,
												backgroundColor: column.bodyBackgroundColor ?? "inherit",
												".MuiTableRow-root.MuiTableRow-hover:hover &": {
													backgroundColor: column.bodyBackgroundColor ?? "#ebebeb", // Default backgroundColor of hovered rows is transparent
												},
											}),
									}}
								>
									{column.truncateString ? (
										<div
											style={{
												display: "table",
												tableLayout: "fixed",
												width: "100%",
											}}
										>
											<div
												style={{
													display: "table-cell",
													textOverflow: "ellipsis",
													overflow: "hidden",
													whiteSpace: "nowrap",
													margin: "auto",
												}}
											>
												{column.subRowCell && typeof column.subRowCell === "function"
													? column.subRowCell(subRow)
													: column.subRowCell}
											</div>
										</div>
									) : column.subRowCell && typeof column.subRowCell === "function" ? (
										column.subRowCell(subRow)
									) : (
										column.subRowCell
									)}
								</TableCell>
							);
						})}
					</TableRow>
				);
			});
		}
	}

	return (
		<React.Fragment>
			<TableRow {...resolvedRowProps} {...interactiveProps} key={`${index}-row`} hover={isRowHoverable}>
				{columns.map((column) => {
					const valign = typeof column.valign === "function" ? column.valign(row) : column.valign;
					return column.hide || column.cell === null ? null : (
						<TableCell
							sx={{
								verticalAlign: valign,
								textOverflow: column.allowBodyWrap ? "revert" : "inherit",
								textWrap: column.allowBodyWrap ? "wrap" : "inherit",
								overflow: column.allowBodyWrap ? "revert" : "inherit",
								...(column.sticky &&
									stickyColumnOffsets && {
										position: "sticky",
										right: stickyColumnOffsets[column.key.toString()],
										zIndex: 1,
										backgroundColor: column.bodyBackgroundColor ?? "inherit",
										".MuiTableRow-root.MuiTableRow-hover:hover &": {
											backgroundColor: column.bodyBackgroundColor ?? "#ebebeb", // Default backgroundColor of hovered rows is transparent
										},
									}),
							}}
							key={`${index}-${column.key}`}
							align={column.alignBody ?? column.align ?? "left"}
							valign={valign}
							colSpan={column.colSpanBody ?? column.colSpan ?? 1}
						>
							<Tooltip
								arrow
								placement="top"
								title={
									column.showTooltip && (
										<Typography
											sx={{
												"& *": {
													color: "#ffffff !important", //overrides the style of the child column.cell when inside a tool tip
												},
												color: "#ffffff",
												fontSize: "13px",
												backGroundColor: "#4C4C4C",
											}}
										>
											{column.cell && typeof column.cell === "function" ? column.cell(row) : column.cell}
										</Typography>
									)
								}
							>
								<Box sx={{ width: column.tableCellContentWidth ?? "fit-content" }}>
									{column.truncateString ? (
										<div
											style={{
												display: "table",
												tableLayout: "fixed",
												width: "100%",
											}}
										>
											<div
												style={{
													display: "table-cell",
													textOverflow: "ellipsis",
													overflow: "hidden",
													whiteSpace: "nowrap",
													margin: "auto",
												}}
											>
												{column.cell && typeof column.cell === "function" ? column.cell(row) : column.cell}
											</div>
										</div>
									) : column.cell && typeof column.cell === "function" ? (
										column.cell(row)
									) : (
										column.cell
									)}
								</Box>
							</Tooltip>
						</TableCell>
					);
				})}
			</TableRow>
			{renderRowSubComponent && open && (
				<TableRow key={`${index}-sub`}>
					<TableCell variant={noSubComponentPadding ? "inner" : undefined} colSpan={totalColSpan}>
						<Collapse in={open} timeout="auto" unmountOnExit>
							<Box>
								{typeof renderRowSubComponent === "function"
									? React.createElement(renderRowSubComponent, {
											...row,
											key: `${row.index}-subcomponent`,
										})
									: renderRowSubComponent}
							</Box>
						</Collapse>
					</TableCell>
				</TableRow>
			)}
			{subRowList}
		</React.Fragment>
	);
}

/**
 * Generates the table according to specifications
 * Recieves table columns as input
 * @returns <React.Fragment>
 */
export function CoreTable<D extends object, K extends keyof D = keyof D>({
	data,
	columns,
	renderRowSubComponent,
	rowProps,
	defaultRowExpanded = false,
	subRowDataProp,
	// pagination properties
	rowsPerPage = 8,
	defaultPage = 0,
	hidePaginationOnSinglePage = true,
	paginationSize = "medium",
	paginationWrap = false,
	// no data properties
	loading = false,
	noResults = false,
	hideNoResultsImage = false,
	noResultsTitle,
	noResultsString = "Tables Unavailable.",
	showNoResultsOnEmptyData = true,
	// style properties
	rowHeight = 60,
	width = "100%",
	maxHeight,
	minHeight,
	lastRowRounded = false,
	hideOuterBorder = false,
	hideInnerBorder = false,
	padding,
	tableMargin,
	fontSize = "15px",
	headerFontSize = "15px",
	noSubComponentPadding = false,
	hover = true,
	boldHeader = false,
	hideHeader = false,
	stickyHeader = false,
	backgroundColor = "#ffffff",
	headerBackgroundColor = "#f7f6fb",
	bodyBackgroundColor = "#ffffff",
	resetPageOnDataChange = false,
	// sort properties
	initialSortOrder = "asc",
	defaultSortOrder = "asc",
	initialSortKey,
	sortRequired = false,
	// Loading option
	useSkeleton = true,
	allowHeaderWrap = false,
	allowBodyWrap = false,
	allowUIOverrides = false,
	unhoverableRows,
	fixedLayout = false,
	id,
	onRowClick,
}: {
	/** The data displayed in the component. */
	data: D[];
	/** The column options of the component. */
	columns: CoreTableColumn<D, K>[];
	/** A component or function that determines the contents of each row's sub component.
	 *  If `renderRowSubComponent` is a function, to ensure the sub component does not
	 *  constantly re-create, pass all the sub component's properties through `row`
	 *  (i.e. the function should not change).
	 *  @param row Contains the data, and other properties, of the row.
	 *  @returns A component to render in each cell of the column's rows.
	 */
	renderRowSubComponent?: RowRender<D>;
	/** Additional props for table rows
	 */
	rowProps?: Partial<RestrictedTableRowProps> | ((row: CoreTableRow<D>) => Partial<RestrictedTableRowProps>);
	/** If `true`, all row subcomponents will default to be opened. @default false */
	defaultRowExpanded?: boolean;
	/** A property in the data that will be used to render a sub row. */
	subRowDataProp?: G<K, D>;
	/** A component or function that determines the contents of each row's subrows.
	 *  @param row Contains the data, and other properties, of the row.
	 *  @returns A component to render in each cell of the column's rows.
	 */
	/** The number of rows per page. @default 8 */
	rowsPerPage?: number;
	/** The default, starting page of the table. @default 0 */
	defaultPage?: number;
	/** If `true`, the pagination is not rendered when
	 *  there is only one page of rows to show. @default true
	 * */
	hidePaginationOnSinglePage?: boolean;
	/** The size of the pagination. @default "medium" */
	paginationSize?: "small" | "medium" | "large";
	/** If `true`, if the pagination is too wide for its container,
	 *  the pagination buttons will wrap. @default false
	 */
	paginationWrap?: boolean;
	/** If `true`, a loading spinner will be displayed instead of \
	 *  the table data contents. @default false
	 */
	loading?: boolean;
	/** If `true`, an image and error message will be displayed instead of \
	 *  the table data contents. @default false
	 */
	noResults?: boolean;
	/** If `true`, the image is not displayed when `noResults` is `true`.
	 *  @default true
	 */
	hideNoResultsImage?: boolean;
	/** The title for the text displayed when `noResults` is `true`. */
	noResultsTitle?: ReactNode;
	/** The text displayed when `noResults` is `true`.
	 *  @default "Tables Unavailable."
	 */
	noResultsString?: string;
	/** If `true`, an image and error message will be displayed
	 *  if the table data is empty. @default true
	 */
	showNoResultsOnEmptyData?: boolean;
	/** The height of each row. @default 60 */
	rowHeight?: number;
	/** The width of the table. @default "100%" */
	width?: string | number;
	/** The max height of the table. */
	maxHeight?: string | number;
	/** The min height of the table. */
	minHeight?: string | number;
	/** If `false`, the bottom corners of the table will not be rounded.
	 *  @default false
	 */
	lastRowRounded?: boolean;
	/** If `true`, the table will not have an outer border.
	 *  @default false
	 */
	hideOuterBorder?: boolean;
	/** If `true`, the table will not have inner borders.
	 *  @default false
	 */
	hideInnerBorder?: boolean;
	/**
	 * The padding of each cell. @default "20px"
	 */
	padding?: string | number;
	/** The font size. */
	fontSize?: string | number;
	/** The header font size. @default "15px" */
	headerFontSize?: string | number;
	/** If `true`, the subcomponent will render without extra padding. @default false */
	noSubComponentPadding?: boolean;
	/** If `true`, the table row will shade on hover, with the exception of unhoverableRows. @default true */
	hover?: boolean;
	/** If `true`, the table header will be bold. @default true */
	boldHeader?: boolean;
	/** If `true`, the table header will not render. @default false */
	hideHeader?: boolean;
	/** If `true`, the table header will stick to the top. @default false */
	stickyHeader?: boolean;
	/** The background color of the table. @default "#ffffff" */
	backgroundColor?: string;
	/** The background color of the table header. @default "#f7f6fb" */
	headerBackgroundColor?: string;
	/** The background color of the table body. @default "#ffffff" */
	bodyBackgroundColor?: string;
	/** If `true`, the page will reset to `defaultPage` when `data` changes. @default false */
	resetPageOnDataChange?: boolean;
	/** Sets the initial sort order. @default "asc" */
	initialSortOrder?: Order;
	/** Sets the default sort order when selecting a sort. @default "asc" */
	defaultSortOrder?: Order;
	/** Sets the initial sort key. */
	initialSortKey?: React.Key;
	/** If `true`, the table will not allow no sort to be applied. @default false */
	sortRequired?: boolean;
	/** If `true`, a skeleton loader will be displayed rather than a circular loader @default true */
	useSkeleton?: boolean;
	/** If `true`, allow text to wrap in the table header cells @default false */
	allowHeaderWrap?: boolean;
	/** If `true`, allow text to wrap in the table body cells @default false */
	allowBodyWrap?: boolean;
	/** Enable overriding default font, color, padding, border @default false */
	allowUIOverrides?: boolean;
	/** Specifies the margin of the whole table */
	tableMargin?: string | number;
	/** An array of indices of rows that are not hoverable */
	unhoverableRows?: number[];
	/** Whether to use table-layout: fixed */
	fixedLayout?: boolean;
	/**Id of table */
	id?: string;
	/** Runs when the row is clicked or pressed via keyboard; adds role="button", tabindex: 0, and focus styles */
	onRowClick?: (row: CoreTableRow<D>) => void;
}) {
	const theme = createTheme({
		// Color Properties
		palette: {
			text: {
				primary: "rgba(0, 0, 0, 0.87)",
			},
			background: {
				paper: allowUIOverrides ? (backgroundColor ? backgroundColor : "#ffffff") : "#ffffff",
				default: allowUIOverrides ? (backgroundColor ? backgroundColor : "#ffffff") : "#ffffff",
			},
		},

		// Specific table component properties - Override styling here
		// Components are listed in heirarchal order -- Child components will override parents attributes
		components: {
			// Table Container
			MuiTableContainer: {
				styleOverrides: {
					root: {
						width: width ? `calc(${width}${hideOuterBorder ? "" : " - 2px"})` : undefined,

						border: allowUIOverrides ? (hideOuterBorder ? "none" : "1px solid #e0e0e0") : "1px solid #e0e0e0",

						borderRadius: allowUIOverrides
							? hideOuterBorder
								? 0
								: lastRowRounded
									? 10
									: "10px 10px 0px 0px"
							: "10px 10px 0px 0px",

						"::-webkit-scrollbar": {
							height: "10px",
							width: "10px",
						},
						"::-webkit-scrollbar-track": {
							background: "#ffffff",
							boxShadow: "none",
							borderRadius: "12px",
						},
						"::-webkit-scrollbar-thumb": {
							background: "rgb(232, 232, 232)",
							outline: "none",
							borderRadius: "6px",
						},
						"::-webkit-scrollbar-thumb:hover": {
							background: "rgb(220, 220, 220)",
						},
					},
				},
			},

			// Table
			MuiTable: {
				styleOverrides: {
					root: {
						fontSize: allowUIOverrides ? (fontSize ? fontSize : "15px") : "15px",
					},
				},
			},

			// Header
			MuiTableHead: {
				styleOverrides: {
					root: {
						fontSize: allowUIOverrides ? headerFontSize : "15px",
						fontWeight: allowUIOverrides ? (boldHeader ? 700 : 400) : 400,
						backgroundColor: allowUIOverrides ? (headerBackgroundColor ? headerBackgroundColor : "#f7f6fb") : "#f7f6fb",
						textWrap: allowUIOverrides ? (allowHeaderWrap ? "wrap" : "nowrap") : "nowrap",
					},
				},
			},

			// Body
			MuiTableBody: {
				styleOverrides: {
					root: {
						fontSize: allowUIOverrides ? (fontSize ? fontSize : "15px") : "15px",
						backgroundColor: allowUIOverrides ? (bodyBackgroundColor ? bodyBackgroundColor : "#ffffff") : "#ffffff",
						textWrap: allowUIOverrides ? (allowBodyWrap ? "wrap" : "nowrap") : "nowrap",
						overflow: allowUIOverrides ? (allowBodyWrap ? "auto" : "hidden") : "hidden",
					},
				},
			},

			// Table Row
			MuiTableRow: {
				styleOverrides: {
					root: {
						border: allowUIOverrides ? (hideInnerBorder ? "0px" : "1px solid #e0e0e0") : "1px solid #e0e0e0",
					},
				},
			},

			// Cell
			MuiTableCell: {
				styleOverrides: {
					root: {
						padding: allowUIOverrides
							? padding
								? padding
								: "16px max(24px, min(40px, 2.6vw))"
							: "16px max(24px, min(40px, 2.6vw))",

						height: allowUIOverrides ? (rowHeight ? rowHeight : "60px") : "60px",

						minHeight: allowUIOverrides ? (rowHeight ? rowHeight : "60px") : "60px",
					},
				},
			},
		},
	});

	const [order, setOrder] = React.useState<Order>(initialSortOrder);
	const [orderBy, setOrderBy] = React.useState<React.Key | null>(initialSortKey ?? null);
	const [descendingComparator, setDescendingComparator] = React.useState<((a: D, b: D) => number) | null>(
		() => columns.find((x) => x.key === initialSortKey)?.descendingComparator ?? null,
	);

	const handleRequestSort = (
		_event: React.MouseEvent<unknown>,
		key: React.Key,
		newDescendingComparator: (a: D, b: D) => number,
	) => {
		const isDesc = orderBy === key && order === "asc";
		const unorder = sortRequired ? false : orderBy === key && order === "desc";
		setOrder(isDesc ? "desc" : "asc");
		setOrderBy(unorder ? null : key);
		setDescendingComparator(() => (a: D, b: D) => newDescendingComparator(a, b));
		setKeepClosed(true);
	};

	const [showLoading, setShowLoading] = React.useState(loading);
	const [showNoResults, setShowNoResults] = React.useState(false);
	React.useEffect(() => {
		setShowNoResults(noResults || (showNoResultsOnEmptyData && !loading && data.length === 0));
		setShowLoading(loading);
	}, [data, loading, noResults, showNoResultsOnEmptyData]);

	const [page, setPage] = React.useState(defaultPage);
	const [pageCount, setPageCount] = React.useState(Math.ceil(data.length / (rowsPerPage > 0 ? rowsPerPage : 1)));
	const [keepClosed, setKeepClosed] = React.useState(false); // used to reset opened subcomponents on page change
	const handleChangePage = (_event: unknown, newPage: number) => {
		setPage(newPage - 1);
		setKeepClosed(true);
	};
	React.useEffect(() => {
		const count = showLoading || showNoResults ? 0 : Math.ceil(data.length / rowsPerPage);
		setPageCount(count);
		if (resetPageOnDataChange) {
			setPage(defaultPage);
		}
	}, [data, showLoading, showNoResults, rowsPerPage, resetPageOnDataChange, defaultPage]);
	React.useEffect(() => {
		if (keepClosed) {
			setKeepClosed(false);
		}
	}, [keepClosed]);
	React.useEffect(() => {
		const maxPage = Math.max(0, pageCount - 1);
		if (page > maxPage) {
			setPage(maxPage);
		}
	}, [page, pageCount]);

	// used to span a cell through all columns of its row
	const totalColSpan = columns.reduce((a, b) => a + (b.colSpan ?? 1), 0);

	// Get the colspan of each column
	const allColSpans: number[] = columns.map((col) => col.colSpan ?? 1);

	const allRows = useMemo(() => {
		const getComparator = (order: Order, descendingComparator: (a: D, b: D) => number): ((a: D, b: D) => number) => {
			return order === "desc" ? (a, b) => descendingComparator(a, b) : (a, b) => -descendingComparator(a, b);
		};

		return orderBy && descendingComparator !== null
			? stableSort(data, getComparator(order, descendingComparator))
			: data;
	}, [orderBy, descendingComparator, data, order]);

	const visibleRows = useMemo(
		() => allRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
		[allRows, page, rowsPerPage],
	);

	const [stickyColumnOffsets, setStickyColumnOffsets] = React.useState<Record<string, number>>({});
	const headerCellRefs = React.useRef<Record<string, HTMLTableCellElement | null>>({});

	React.useLayoutEffect(() => {
		const newOffsets: Record<string, number> = {};
		const stickyColumns = columns.filter((column) => column.sticky).reverse(); // Calculating the right offset, so we iterate in reverse
		let accumulatedOffset = -1; // Fixes an issue where the rightmost sticky column is not aligned with the right border of the table

		for (const column of stickyColumns) {
			const headerCell = headerCellRefs.current[column.key.toString()];
			if (headerCell) {
				newOffsets[column.key.toString()] = accumulatedOffset;
				accumulatedOffset += headerCell.offsetWidth;
			}
		}

		setStickyColumnOffsets(newOffsets);
	}, [columns, data]);

	// Merge with main theme to include font
	const mergedTheme = useMemo(() => createTheme(themeOptions, theme), [theme]);
	return (
		<ThemeProvider theme={mergedTheme}>
			<div
				style={{
					overflowX: "auto",
					width: "100%",
					maxHeight: maxHeight,
					minHeight: minHeight,
					display: "flex",
					margin: tableMargin,
				}}
			>
				<TableContainer component={Paper}>
					<Table id={id} stickyHeader={stickyHeader} sx={{ tableLayout: fixedLayout ? "fixed" : "auto" }}>
						{hideHeader ? null : (
							<TableHead key="head">
								<HeaderContent
									columns={columns}
									onRequestSort={handleRequestSort}
									order={order}
									orderBy={orderBy}
									defaultSortOrder={defaultSortOrder}
									stickyColumnOffsets={stickyColumnOffsets}
									headerCellRefs={headerCellRefs}
								/>
							</TableHead>
						)}
						<TableBody key="body">
							{showLoading ? (
								useSkeleton ? (
									<LoadingRowSkeleton allColSpans={allColSpans} rowsPerPage={rowsPerPage} />
								) : (
									<LoadingRow colSpan={totalColSpan} />
								)
							) : showNoResults ? (
								<NoResultsRow
									noResultsTitle={noResultsTitle}
									noResultsString={noResultsString}
									hideNoResultsImage={hideNoResultsImage}
									colSpan={totalColSpan}
								/>
							) : (
								visibleRows.map((row, index) => (
									<BodyRowContent
										columns={columns}
										rowData={row}
										renderRowSubComponent={renderRowSubComponent}
										defaultRowExpanded={defaultRowExpanded}
										rowProps={rowProps}
										subRowDataProp={subRowDataProp}
										index={index}
										absoluteIndex={page * rowsPerPage + index}
										totalColSpan={totalColSpan}
										key={index}
										keepClosed={keepClosed}
										noSubComponentPadding={noSubComponentPadding}
										hover={hover}
										paginationPage={page}
										rowsPerPage={rowsPerPage}
										unhoverableRows={unhoverableRows}
										stickyColumnOffsets={stickyColumnOffsets}
										onRowClick={onRowClick}
									/>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</div>

			{hidePaginationOnSinglePage && pageCount <= 1 ? null : (
				<div className={styles["pagination-wrapper"]}>
					<CorePagination
						count={pageCount}
						onChange={handleChangePage}
						defaultPage={defaultPage + 1}
						size={paginationSize}
						page={page + 1}
						paginationWrap={paginationWrap}
					/>
				</div>
			)}
		</ThemeProvider>
	);
}

// Declare new variants here
declare module "@mui/material/TableCell" {
	interface TableCellPropsVariantOverrides {
		inner: true;
	}
}

export function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
	const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}
